/**
 * Export utilities for CSV and PDF generation
 * CSV is generated synchronously
 * PDF uses lazy-loaded jspdf to avoid bundle bloat
 */

/**
 * Generate CSV string from data array (pure function - no DOM required)
 * @param data Array of objects to export
 * @returns CSV string
 */
export function generateCSVString<T extends Record<string, unknown>>(data: T[]): string {
  if (data.length === 0) {
    return '';
  }

  // Get headers from the first object's keys
  const headers = Object.keys(data[0]);
  
  // Build CSV content
  const csvRows: string[] = [];
  
  // Add header row
  csvRows.push(headers.join(','));
  
  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      // Escape quotes and wrap in quotes if contains comma or quote
      const stringValue = String(value ?? '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
}

/**
 * Export data as a CSV file download
 * @param data Array of objects to export
 * @param filename Filename for the download (without extension)
 */
export function exportCSV<T extends Record<string, unknown>>(data: T[], filename: string): string {
  const csvString = generateCSVString(data);
  
  // Only trigger download in browser environment
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  return csvString;
}

/**
 * Export data as a PDF file download
 * Uses lazy-loaded jspdf to avoid increasing bundle size
 * @param data Array of objects to export
 * @param title Title for the PDF document
 */
export async function exportPDF<T extends Record<string, unknown>>(
  data: T[],
  title: string
): Promise<void> {
  // Lazy load jspdf only when exportPDF is called
  const { jsPDF } = await import('jspdf');
  
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.text(title, 14, 22);
  
  // Add date
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
  
  if (data.length === 0) {
    doc.text('No data available', 14, 40);
  } else {
    // Get headers from the first object's keys
    const headers = Object.keys(data[0]);
    
    // Set up table dimensions
    const startY = 40;
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const colWidth = (pageWidth - 2 * margin) / headers.length;
    
    // Add headers
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    headers.forEach((header, i) => {
      doc.text(String(header), margin + i * colWidth, startY);
    });
    
    // Add data rows
    doc.setFont('helvetica', 'normal');
    let y = startY + 10;
    
    for (const row of data) {
      // Check if we need a new page
      if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = 20;
      }
      
      headers.forEach((header, i) => {
        const value = String(row[header] ?? '');
        // Truncate if too long
        const maxWidth = colWidth - 2;
        const truncated = value.length > 20 ? value.substring(0, 17) + '...' : value;
        doc.text(truncated, margin + i * colWidth, y);
      });
      y += 7;
    }
  }
  
  // Trigger download
  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}
